import type { CSSProperties, MouseEvent } from 'react';
import type { RunAction } from './AddClient.tsx';
import { api, type Severity, type Tenant, type TenantScore } from './api.ts';
import {
  CONNECTION,
  DrawerRow,
  ManageButton,
  hasRowActions,
  type Capabilities,
  type DrawerKind,
} from './ClientActions.tsx';
import {
  SEVERITY_TEXT,
  SORTS,
  STALE_DAYS,
  VERDICTS,
  asAt,
  flip,
  relativeDays,
  type Row,
  type SortKey,
} from './clientRows.ts';
import { message } from './format.ts';
import { StaffScoreLine } from './platform/ScoreParts.tsx';

/**
 * One client, as the assessment table draws it — plus the two header pieces
 * that belong to the same table.
 *
 * Beside `Portfolio.tsx` rather than inside it because that file holds the
 * screen: what is loaded, which view is showing, and what a mutation does. This
 * one holds a row, which is a separate question and a long answer. Neither
 * imports the other; both read the row model from `clientRows.ts`.
 */

interface SkeletonRowProps {
  readonly selectable: boolean;
  readonly manageable: boolean;
}

export const SkeletonRow = ({ selectable, manageable }: SkeletonRowProps) => (
  <tr className="skeleton-row">
    {selectable && <td className="skeleton-cell" style={{ '--sk-w': '20%' } as CSSProperties} />}
    <td className="skeleton-cell" style={{ '--sk-w': '75%' } as CSSProperties} />
    <td className="skeleton-cell" style={{ '--sk-w': '55%' } as CSSProperties} />
    <td className="skeleton-cell" style={{ '--sk-w': '45%' } as CSSProperties} />
    <td className="skeleton-cell" style={{ '--sk-w': '40%' } as CSSProperties} />
    <td className="skeleton-cell" style={{ '--sk-w': '70%' } as CSSProperties} />
    {manageable && <td className="skeleton-cell" style={{ '--sk-w': '60%' } as CSSProperties} />}
  </tr>
);

interface SortHeaderProps {
  column: SortKey;
  label: string;
  /** The `cl-col-*` width class this header carries; see portfolio.css. */
  width: string;
  sort: SortKey;
  reversed: boolean;
  onSort: (key: SortKey) => void;
}

export const SortHeader = ({ column, label, width, sort, reversed, onSort }: SortHeaderProps) => {
  const active = column === sort;
  const natural = SORTS[column].direction;
  const ariaSort = !active ? 'none' : reversed ? flip(natural) : natural;

  return (
    <th scope="col" aria-sort={ariaSort} className={`cl-sortable cl-col-${width}`}>
      <button type="button" className="cl-sort" onClick={() => onSort(column)}>
        {label}
        <span className="cl-arrow" aria-hidden="true">
          {!active ? '↕' : ariaSort === 'ascending' ? '↑' : '↓'}
        </span>
      </button>
    </th>
  );
};

interface SeverityBadgeProps {
  severity: Severity;
}

/** Exactly four marks, the first N filled — the contract `.sev` is built on. */
const SeverityBadge = ({ severity }: SeverityBadgeProps) => (
  <span className={`sev sev-${severity}`}>
    <span className="sev-ticks" aria-hidden="true">
      <span />
      <span />
      <span />
      <span />
    </span>
    {SEVERITY_TEXT[severity]}
  </span>
);

/**
 * Whose client this is, on a platform owner's console, where every account's
 * clients share one list. Worded as a label rather than a colour alone so that
 * it reads the same to somebody who cannot tell the colours apart.
 */
export const AccountBadge = ({ name }: { readonly name: string | null }) => (
  <span className="cl-account" title="The customer account this client belongs to">
    <span className="cl-account-label">Account</span>
    {name ?? 'Unknown account'}
  </span>
);

interface TenantRowProps {
  readonly row: Row;
  readonly capabilities: Capabilities;
  readonly busy: boolean;
  /** How many columns the table has, so an open drawer spans all of them. */
  readonly columns: number;
  readonly checked: boolean;
  readonly onToggleSelected: (id: string) => void;
  readonly drawer: DrawerKind | null;
  readonly onOpenRow: (tenantId: string, kind: DrawerKind | null) => void;
  readonly onOpen: (tenant: Tenant) => void;
  readonly onError: (message: string) => void;
  readonly onRun: RunAction;
  /**
   * Platform staff only (ADR-0081). The screen passes null for everybody else
   * — it comes from `staffScoreFor`, which cannot return one to a customer —
   * and a null draws nothing at all, so a customer's row is the markup it was
   * before scores existed.
   */
  readonly staffScore?: TenantScore | null;
}

export const TenantRow = ({
  row,
  capabilities,
  busy,
  columns,
  checked,
  onToggleSelected,
  drawer,
  onOpenRow,
  onOpen,
  onError,
  onRun,
  staffScore = null,
}: TenantRowProps) => {
  const { tenant, risk, age, scannedAt, counts, broken } = row;
  const connection = CONNECTION[tenant.connectionState];
  const stale = age !== null && age > STALE_DAYS;

  /**
   * The whole row is a click target for the mouse, and the client's name is a
   * real button for everything else. A row that only responds to a click is a
   * row a keyboard cannot reach, and putting `role="button"` on the `<tr>`
   * would trade that for destroying the table semantics a screen reader needs
   * to announce which column it is in.
   *
   * `input` and `label` joined `button` and `a` in the guard when the row grew
   * a selection box: without them, ticking a client to archive it would open
   * that client instead.
   */
  const activate = (event: MouseEvent<HTMLTableRowElement>) => {
    if ((event.target as HTMLElement).closest('button, a, input, label') !== null) return;
    onOpen(tenant);
  };

  const openStoredReport = () => {
    if (tenant.latestScanId === null) return;
    void api
      .openReport(tenant.id, tenant.latestScanId)
      .catch((cause: unknown) => onError(message(cause)));
  };

  return (
    <>
      <tr className={broken ? 'cl-row dimmed' : 'cl-row'} onClick={activate}>
        {capabilities.canManage && (
          <td className="admin-check-col">
            <input
              type="checkbox"
              className="admin-check"
              aria-label={`Select ${tenant.displayName}`}
              checked={checked}
              disabled={busy}
              onChange={() => onToggleSelected(tenant.id)}
            />
          </td>
        )}

        <th scope="row" className="cl-client">
          <button
            type="button"
            className="cl-name"
            aria-label={`Open ${tenant.displayName}`}
            onClick={() => onOpen(tenant)}
          >
            {tenant.displayName}
          </button>
          {/* Microsoft's own identifier, verbatim and never truncated: it is the
              string the operator pastes into the admin centre. */}
          <span className="mono cl-id">{tenant.entraTenantId}</span>
          {tenant.accountName !== undefined && <AccountBadge name={tenant.accountName} />}
          {staffScore !== null && <StaffScoreLine score={staffScore} />}
        </th>

        <td>
          <span className={`cl-flag cl-flag-${risk.flag}`}>{risk.label}</span>
          <p className="note cl-action">{risk.action}</p>
        </td>

        <td>
          <span className={`conn ${connection.chip}`}>{connection.text}</span>
          {tenant.connectionDetail !== null && risk.tier !== 0 && (
            <p className="note cl-action">{tenant.connectionDetail}</p>
          )}
        </td>

        <td>
          {scannedAt !== null && age !== null ? (
            <>
              <span className="cl-when">{relativeDays(age)}</span>
              <span className="note cl-date num">{asAt(scannedAt)}</span>
              {stale && <span className="cl-stale">Older than a month</span>}
            </>
          ) : (
            <>
              <span className="cl-when cl-never">Never</span>
              <p className="note cl-action">
                {tenant.connectionState === 'connected'
                  ? 'Nothing has been assessed for this client.'
                  : 'No assessment can run until the connection works.'}
              </p>
            </>
          )}
        </td>

        <td>
          {scannedAt === null ? (
            <span className="note">—</span>
          ) : (
            <>
              <ul className="cl-counts">
                {VERDICTS.filter((entry) => counts[entry.verdict] > 0).map((entry) => (
                  <li key={entry.verdict}>
                    <span className={`verdict ${entry.className}`}>{entry.label}</span>
                    <span className="cl-count num">{counts[entry.verdict]}</span>
                  </li>
                ))}
              </ul>

              {tenant.worstFailingSeverity !== null && (
                <p className="cl-worst">
                  <span className="sr-only">Worst failing severity: </span>
                  <SeverityBadge severity={tenant.worstFailingSeverity} />
                </p>
              )}

              {/* A broken connection does not invalidate the last assessment —
                  it only stops the next one. The stored report is still the
                  truth about the day it was produced, so it stays reachable
                  here, stamped with that date rather than presented as
                  current. */}
              {broken && (
                <p className="cl-frozen">
                  <span>As at {asAt(scannedAt)} — nothing is refreshing these.</span>
                  {tenant.latestScanId !== null && (
                    <button
                      type="button"
                      className="btn btn-quiet btn-sm"
                      onClick={openStoredReport}
                    >
                      Open that report
                    </button>
                  )}
                </p>
              )}
            </>
          )}
        </td>

        {hasRowActions(capabilities) && (
          <td>
            <ManageButton tenant={tenant} open={drawer} busy={busy} onOpen={onOpenRow} />
          </td>
        )}
      </tr>

      {drawer !== null && (
        <DrawerRow
          kind={drawer}
          tenant={tenant}
          archived={false}
          capabilities={capabilities}
          busy={busy}
          columns={columns}
          onOpen={onOpenRow}
          onRun={onRun}
        />
      )}
    </>
  );
};
